import type {JobMatchQueryParam} from 'src/types/job-variables';


export const JOB_WITH_LINK_IDENTIFIER = 'matched_job/title_with_link';
/** this regex only check the presence of text like
 * - '<foo>'
 * - '<foo|fallback: `bar`>',
 * - '<foo|extra:"bar"|fallback:"'baz'">' in the text
 * copied from src/components/lib/markdown-editor/utils.js
 * used for validating the presence of job variables in the text.
 */
export const JOB_MATCH_VARIABLE_REGEX: RegExp = /<+([^>]+)>+|\{\{([^}]+)\}\}/g;

export function* findJobVariablesInString(
  string: string,
): Generator<string, void, void> {
  let match;
  while ((match = JOB_MATCH_VARIABLE_REGEX.exec(string)) !== null) {
    yield match[1] || match[2];
  }
}

// Filter Dependencies (customization rules)
const VISIBILITY_DISABLED = 'DISABLED';

export function hasCommonElement(
  includedFilters: Set<string>,
  dependencies: string[],
): boolean {
  return dependencies.some((id) => includedFilters.has(id));
}

export function getAvailableJobMatchFilters(
  filterParams: JobMatchQueryParam[],
  includedFilters: Set<string>,
): JobMatchQueryParam[] {
  return filterParams.filter((param) => {
    if (param.visibility === VISIBILITY_DISABLED) {
      return false;
    }

    const hasDependencies =
      Array.isArray(param.depends_on_either) &&
      param.depends_on_either.length > 0;

    if (hasDependencies) {
      return hasCommonElement(includedFilters, param.depends_on_either);
    }

    return true;
  });
}

export function removeJobMatchFilterAndDependants(
  filterId: string,
  currentFilters: Set<string>,
  availableFilters: JobMatchQueryParam[],
): Set<string> {
  let updatedFilters = new Set(currentFilters);
  updatedFilters.delete(filterId);

  const dependants = availableFilters.filter(
    (param) =>
      Array.isArray(param.depends_on_either) &&
      param.depends_on_either.includes(filterId) &&
      updatedFilters.has(param.id),
  );

  for (const param of dependants) {
    const isValid = hasCommonElement(
      updatedFilters,
      param.depends_on_either || [],
    );
    if (!isValid) {
      updatedFilters = removeJobMatchFilterAndDependants(
        param.id,
        updatedFilters,
        availableFilters,
      );
    }
  }

  return updatedFilters;
}

// Filter Categories (customization rules)
export type FilterCategory =
  | 'qualification'
  | 'location'
  | 'preferences'
  | 'custom';

export interface CategorizedFilter {
  category: FilterCategory;
  categoryLabel: string;
  filters: JobMatchQueryParam[];
}

const QUALIFICATION_FILTERS = [
  'title',
  'skills',
  'keywords',
  'discipline',
  'specialties',
  'certifications',
] as const;

const LOCATION_FILTERS = [
  'zip',
  'city',
  'state',
  'country',
  'distance',
  'distance_unit',
] as const;

const PREFERENCES_FILTERS = [
  'worksite',
  'compensation_amount_min',
  'contract_duration_min',
  'shifts',
  'contract_duration_unit',
  'compensation_currency',
  'compensation_frequency',
] as const;

const CATEGORY_LABELS: Record<FilterCategory, string> = {
  qualification: 'Qualification',
  location: 'Location',
  preferences: 'Preferences',
  custom: 'Custom Fields',
};

function getFilterCategory(filterId: string): FilterCategory {
  const normalizedId = filterId.toLowerCase();

  if (QUALIFICATION_FILTERS.some((f) => normalizedId.includes(f))) {
    return 'qualification';
  }
  if (LOCATION_FILTERS.some((f) => normalizedId.includes(f))) {
    return 'location';
  }
  if (PREFERENCES_FILTERS.some((f) => normalizedId.includes(f))) {
    return 'preferences';
  }

  return 'custom';
}

export function categorizeFilters(
  filters: JobMatchQueryParam[],
): CategorizedFilter[] {
  const categories: Record<FilterCategory, JobMatchQueryParam[]> = {
    qualification: [],
    location: [],
    preferences: [],
    custom: [],
  };

  for (const filter of filters) {
    const category = getFilterCategory(filter.id);
    categories[category].push(filter);
  }

  return Object.entries(categories)
    .filter(([, filters]) => filters.length > 0)
    .map(([category, filters]) => ({
      category: category as FilterCategory,
      categoryLabel: CATEGORY_LABELS[category as FilterCategory],
      filters,
    }));
}

// Filter Enablement Logic
export function isFilterEnabled(
  filterId: string,
  includedFilters: Set<string>,
  allFilters: JobMatchQueryParam[],
): boolean {
  const filter = allFilters.find((f) => f.id === filterId);
  if (filter?.depends_on_either && filter.depends_on_either.length > 0) {
    return hasCommonElement(includedFilters, filter.depends_on_either);
  }
  return true;
}

export function findCommonElements(
  includedFilters: Set<string>,
  dependencies: string[] | undefined | null,
): boolean {
  if (!dependencies || dependencies.length === 0) {
    return false;
  }
  return hasCommonElement(includedFilters, dependencies);
}

export const isJobMatch = (variable: string): boolean => {
  return (
    typeof variable === 'string' &&
    (variable.includes('job_match_fetch') ||
      variable.includes('jobMatchFormatter'))
  );
};
